import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SignatureModal from '../components/SignatureModal';
import { UnitTab, SignatureField } from '../components/POEUnit';
import { toast } from 'sonner';
import { ArrowLeft, FileDown, Lock, Unlock, Loader2, PenLine, GraduationCap } from 'lucide-react';
import { PROGRAMME_OPTIONS } from '../lib/poeConstants';

const qkey = (code) => (code || '').replace(/\//g, '_').replace(/\./g, '_');

const StudentPOEPage = () => {
    const { studentId } = useParams();
    const { getAuthHeaders, API } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('overview');
    const [qualFilter, setQualFilter] = useState('all');
    const [grades, setGrades] = useState({});
    const [units, setUnits] = useState({});
    const [declarations, setDeclarations] = useState({});
    const [checklist, setChecklist] = useState({});
    const [cover, setCover] = useState({});
    const [locked, setLocked] = useState(false);
    const [sigModal, setSigModal] = useState({ open: false, handler: null, title: '' });

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/poe/student/${studentId}`, { headers: getAuthHeaders() });
            setData(res.data);
            const a = res.data.assignment || {};
            setGrades(a.grades || {});
            setUnits(a.units || {});
            setDeclarations(a.declarations || {});
            setChecklist(a.checklist || {});
            setCover(a.cover || {});
            setLocked(a.locked || false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load POE');
        } finally {
            setLoading(false);
        }
    }, [API, studentId, getAuthHeaders]);

    useEffect(() => { load(); }, [load]);

    const saveGrade = async (outcomeId, score, date) => {
        if (locked) return;
        const next = { ...grades };
        if (score === null || score === undefined) delete next[outcomeId];
        else next[outcomeId] = { ...(next[outcomeId] || {}), score, date: date ?? next[outcomeId]?.date ?? '' };
        setGrades(next);
        try {
            await axios.post(`${API}/poe/student/${studentId}/grade`, { outcome_id: outcomeId, score, date: next[outcomeId]?.date || '' }, { headers: getAuthHeaders() });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        }
    };

    const saveUnitMeta = async (unitId, fields) => {
        if (locked) return;
        setUnits(prev => ({ ...prev, [unitId]: { ...(prev[unitId] || {}), ...fields } }));
        try {
            await axios.post(`${API}/poe/student/${studentId}/unit-meta`, { unit_id: unitId, ...fields }, { headers: getAuthHeaders() });
        } catch (e) {
            toast.error('Failed to save');
        }
    };

    const saveDeclaration = async (code, fields) => {
        if (locked) return;
        const k = qkey(code);
        setDeclarations(prev => ({ ...prev, [k]: { ...(prev[k] || {}), ...fields } }));
        try {
            await axios.post(`${API}/poe/student/${studentId}/declaration`, { qualification_code: code, ...fields }, { headers: getAuthHeaders() });
        } catch (e) {
            toast.error('Failed to save');
        }
    };

    const saveChecklistSig = async (code, unitRef, role, signature) => {
        if (locked) return;
        const k = qkey(code);
        const u = String(unitRef).replace(/ /g, '_');
        setChecklist(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [u]: { ...((prev[k] || {})[u] || {}), [`${role}_signature`]: signature } } }));
        try {
            await axios.post(`${API}/poe/student/${studentId}/checklist`, { qualification_code: code, unit_ref: unitRef, role, signature }, { headers: getAuthHeaders() });
        } catch (e) {
            toast.error('Failed to save');
        }
    };

    const saveCover = async (fields) => {
        if (locked) return;
        setCover(prev => ({ ...prev, ...fields }));
        try {
            await axios.post(`${API}/poe/student/${studentId}/cover`, fields, { headers: getAuthHeaders() });
        } catch (e) {
            toast.error('Failed to save');
        }
    };

    const toggleLock = async () => {
        try {
            const res = await axios.post(`${API}/poe/student/${studentId}/lock`, { locked: !locked }, { headers: getAuthHeaders() });
            setLocked(res.data.locked);
            toast.success(res.data.locked ? 'POE locked' : 'POE unlocked');
        } catch (e) {
            toast.error('Failed to update lock');
        }
    };

    const exportPdf = async () => {
        try {
            const q = qualFilter !== 'all' ? `?qualification=${encodeURIComponent(qualFilter)}` : '';
            const res = await axios.get(`${API}/poe/student/${studentId}/pdf${q}`, { headers: getAuthHeaders(), responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `POE_${data?.student?.student_id || studentId}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('POE exported');
        } catch (e) {
            toast.error('Failed to export');
        }
    };

    const openSig = (title, handler) => setSigModal({ open: true, title, handler });

    if (loading) return <Layout><div className="p-10 text-center text-gray-400">Loading POE...</div></Layout>;
    if (!data) return <Layout><div className="p-10 text-center text-gray-400">POE not found</div></Layout>;

    const { student, template, units: unitList } = data;
    const quals = template.qualifications || [];
    const visibleUnits = unitList.filter(u => qualFilter === 'all' || (u.qualifications || []).includes(qualFilter));
    const activeTab = (tab === 'overview' || visibleUnits.some(u => u.id === tab)) ? tab : 'overview';
    const unitsByModule = [];
    visibleUnits.forEach(u => {
        let g = unitsByModule.find(x => x.ref === u.module_ref);
        if (!g) { g = { ref: u.module_ref, name: u.module_name || u.module_ref, units: [] }; unitsByModule.push(g); }
        g.units.push(u);
    });
    const visibleChecklists = (template.checklists || []).filter(cl => qualFilter === 'all' || cl.qualification_code === qualFilter);

    return (
        <Layout>
            <div className="space-y-5 animate-fade-in" data-testid="student-poe-page">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/poe')} data-testid="poe-back-btn">
                            <ArrowLeft className="w-4 h-4 mr-1" /> Back
                        </Button>
                        <div className="w-11 h-11 bg-ica-navy/10 rounded-full flex items-center justify-center">
                            <GraduationCap className="w-5 h-5 text-ica-navy dark:text-ica-light-gold" />
                        </div>
                        <div>
                            <h1 className="font-heading text-2xl font-bold text-ica-navy">{student.name}</h1>
                            <p className="text-sm text-gray-500 dark:text-slate-400">{student.student_id} · {template.name}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={qualFilter} onValueChange={(v) => setQualFilter(v)}>
                            <SelectTrigger className="w-full sm:w-72 h-9" data-testid="poe-qual-filter">
                                <SelectValue placeholder="All Qualifications" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Qualifications</SelectItem>
                                {quals.map(q => (
                                    <SelectItem key={q.code} value={q.code}>{q.title} ({q.code})</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {locked && <span className="text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>}
                        <Button variant="outline" size="sm" onClick={toggleLock} data-testid="poe-lock-btn">
                            {locked ? <><Unlock className="w-4 h-4 mr-1" /> Unlock</> : <><Lock className="w-4 h-4 mr-1" /> Lock</>}
                        </Button>
                        <Button size="sm" onClick={exportPdf} data-testid="poe-export-pdf-btn">
                            <FileDown className="w-4 h-4 mr-1" /> Export PDF
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex flex-col gap-2 border-b border-gray-200 dark:border-slate-700 pb-3">
                    <button onClick={() => setTab('overview')} data-testid="poe-tab-overview"
                        className={`self-start px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === 'overview' ? 'bg-ica-navy text-white' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700'}`}>
                        Cover & Verification
                    </button>
                    {unitsByModule.map(group => (
                        <div key={group.ref} className="flex flex-wrap items-center gap-2" data-testid={`poe-module-group-${group.ref}`}>
                            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-slate-500 pr-1">{group.name}</span>
                            {group.units.map(u => (
                                <button key={u.id} onClick={() => setTab(u.id)} data-testid={`poe-tab-${u.module_ref}-${u.unit_ref}`}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${activeTab === u.id ? 'bg-ica-gold text-white' : 'bg-white dark:bg-slate-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700'}`}>
                                    {u.unit_no}
                                </button>
                            ))}
                        </div>
                    ))}
                </div>

                {activeTab === 'overview' && (
                    <OverviewTab
                        student={student} template={template} checklists={visibleChecklists} cover={cover} declarations={declarations} checklist={checklist}
                        locked={locked} saveCover={saveCover} saveDeclaration={saveDeclaration} saveChecklistSig={saveChecklistSig} openSig={openSig}
                    />
                )}

                {visibleUnits.map(u => activeTab === u.id && (
                    <UnitTab key={u.id} unit={u} template={template} grades={grades} unitMeta={units[u.id] || {}}
                        locked={locked} saveGrade={saveGrade} saveUnitMeta={saveUnitMeta} openSig={openSig} />
                ))}

                <SignatureModal
                    open={sigModal.open}
                    onOpenChange={(o) => setSigModal(s => ({ ...s, open: o }))}
                    title={sigModal.title}
                    onSave={(d) => sigModal.handler && sigModal.handler(d)}
                />
            </div>
        </Layout>
    );
};

const Card = ({ children, className = '' }) => (
    <div className={`bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-5 ${className}`}>{children}</div>
);

const OverviewTab = ({ student, template, checklists, cover, declarations, checklist, locked, saveCover, saveDeclaration, saveChecklistSig, openSig }) => (
    <div className="space-y-5">
        <Card>
            <h2 className="text-base font-semibold text-ica-navy dark:text-ica-light-gold mb-1">{template.subtitle}</h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">{template.evidence_part}</p>
            <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                    <Label className="text-xs">Student Name</Label>
                    <Input value={cover.student_name ?? student.name} disabled={locked} onChange={(e) => saveCover({ student_name: e.target.value })} data-testid="poe-cover-name" />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Student Number</Label>
                    <Input value={cover.student_number ?? student.student_id} disabled={locked} onChange={(e) => saveCover({ student_number: e.target.value })} data-testid="poe-cover-number" />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">ID Number</Label>
                    <Input value={cover.id_number ?? student.id_number ?? ''} disabled={locked} onChange={(e) => saveCover({ id_number: e.target.value })} data-testid="poe-cover-idnumber" />
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">Registered ICA Programme</Label>
                    <Select value={cover.programme ?? student.programme ?? ''} disabled={locked} onValueChange={(v) => saveCover({ programme: v })}>
                        <SelectTrigger data-testid="poe-cover-programme"><SelectValue placeholder="Select programme" /></SelectTrigger>
                        <SelectContent>
                            {PROGRAMME_OPTIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
                <div>
                    <p className="font-semibold text-gray-700 dark:text-slate-200 mb-1">National Qualifications</p>
                    <ul className="list-disc list-inside text-gray-500 dark:text-slate-400 space-y-0.5">
                        {template.national_qualifications.map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                </div>
                <div>
                    <p className="font-semibold text-gray-700 dark:text-slate-200 mb-1">International Qualifications</p>
                    <ul className="list-disc list-inside text-gray-500 dark:text-slate-400 space-y-0.5">
                        {template.international_qualifications.map((q, i) => <li key={i}>{q}</li>)}
                    </ul>
                </div>
            </div>
        </Card>

        {(checklists || template.checklists).map(cl => {
            const k = qkey(cl.qualification_code);
            const dec = declarations[k] || {};
            const cks = checklist[k] || {};
            return (
                <Card key={cl.qualification_code}>
                    <h3 className="text-sm font-semibold text-ica-navy dark:text-ica-light-gold mb-3">{cl.title}</h3>
                    <div className="overflow-x-auto doc-scroll">
                        <table className="w-full text-sm border border-gray-200 dark:border-slate-700">
                            <thead className="bg-ica-navy text-white">
                                <tr>
                                    <th className="px-3 py-2 text-left">Unit</th>
                                    <th className="px-3 py-2 text-left">Description</th>
                                    <th className="px-3 py-2 text-center w-32">Assessor</th>
                                    <th className="px-3 py-2 text-center w-32">Student</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cl.units.map(u => {
                                    const uk = String(u.ref).replace(/ /g, '_');
                                    const row = cks[uk] || {};
                                    return (
                                        <tr key={u.ref} className="border-b border-gray-100 dark:border-slate-700">
                                            <td className="px-3 py-2 font-medium whitespace-nowrap text-gray-800 dark:text-slate-200">{u.ref}</td>
                                            <td className="px-3 py-2 text-gray-600 dark:text-slate-400 text-xs">{u.description}</td>
                                            <td className="px-3 py-2 text-center">
                                                {row.assessor_signature ? (
                                                    <img src={row.assessor_signature} alt="sig" className="h-8 mx-auto object-contain cursor-pointer" onClick={() => !locked && openSig('Assessor Signature', (d) => saveChecklistSig(cl.qualification_code, u.ref, 'assessor', d))} />
                                                ) : (
                                                    <Button variant="ghost" size="sm" disabled={locked} className="h-7 text-xs" onClick={() => openSig('Assessor Signature', (d) => saveChecklistSig(cl.qualification_code, u.ref, 'assessor', d))}><PenLine className="w-3 h-3 mr-1" />Sign</Button>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                {row.student_signature ? (
                                                    <img src={row.student_signature} alt="sig" className="h-8 mx-auto object-contain cursor-pointer" onClick={() => !locked && openSig('Student Signature', (d) => saveChecklistSig(cl.qualification_code, u.ref, 'student', d))} />
                                                ) : (
                                                    <Button variant="ghost" size="sm" disabled={locked} className="h-7 text-xs" onClick={() => openSig('Student Signature', (d) => saveChecklistSig(cl.qualification_code, u.ref, 'student', d))}><PenLine className="w-3 h-3 mr-1" />Sign</Button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Declaration */}
                    <div className="mt-4 p-4 bg-gray-50 dark:bg-slate-700/40 rounded-lg">
                        <p className="text-xs font-semibold text-gray-700 dark:text-slate-200 mb-3 uppercase tracking-wide">Declaration</p>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <div className="space-y-1">
                                <Label className="text-xs">Completion Date</Label>
                                <Input type="date" value={dec.completion_date || ''} disabled={locked} onChange={(e) => saveDeclaration(cl.qualification_code, { completion_date: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Internal Verifier Name</Label>
                                <Input value={dec.iv_name || ''} disabled={locked} onChange={(e) => saveDeclaration(cl.qualification_code, { iv_name: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">IV Date</Label>
                                <Input type="date" value={dec.iv_date || ''} disabled={locked} onChange={(e) => saveDeclaration(cl.qualification_code, { iv_date: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">External Verifier Name (If Sampled)</Label>
                                <Input value={dec.ev_name || ''} disabled={locked} onChange={(e) => saveDeclaration(cl.qualification_code, { ev_name: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">EV Date</Label>
                                <Input type="date" value={dec.ev_date || ''} disabled={locked} onChange={(e) => saveDeclaration(cl.qualification_code, { ev_date: e.target.value })} />
                            </div>
                            <div className="space-y-1">
                                <Label className="text-xs">Student Date</Label>
                                <Input type="date" value={dec.student_date || ''} disabled={locked} onChange={(e) => saveDeclaration(cl.qualification_code, { student_date: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3 mt-3">
                            <SignatureField label="IV Signature" value={dec.iv_signature} disabled={locked} onClick={() => openSig('IV Signature', (d) => saveDeclaration(cl.qualification_code, { iv_signature: d }))} />
                            <SignatureField label="EV Signature" value={dec.ev_signature} disabled={locked} onClick={() => openSig('EV Signature', (d) => saveDeclaration(cl.qualification_code, { ev_signature: d }))} />
                            <SignatureField label="Student Signature" value={dec.student_signature} disabled={locked} onClick={() => openSig('Student Signature', (d) => saveDeclaration(cl.qualification_code, { student_signature: d }))} />
                        </div>
                    </div>
                </Card>
            );
        })}
    </div>
);

export default StudentPOEPage;
